import { TotadminPermission } from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section';

import { AuditLogs, type SubjectLabels } from './index';

/**
 * La section « historique » d'une page de détail, en une ligne.
 *
 * Deux choses viennent de la PAGE, parce qu'elle seule les connaît :
 * - `collectionDocumentIds` : le document et les objets qu'il englobe (une colonne passe aussi ses
 *   étagères et ses emplacements) ;
 * - `subjectLabels` : de quoi nommer ces objets autrement que par leur identifiant.
 *
 * Rien de tout cela n'est calculé au moment de l'audit : la trace ne contient que ce qui a changé.
 *
 * `collectionNames` sert les pages qui n'ont PAS de document — le planning de livraison est une
 * grille de centaines de documents dont aucun n'est le sujet de la page.
 *
 * `lazy` est le second point important : sans lui, les pages qui portent cette section lançaient
 * leur requête au chargement, section repliée.
 *
 * Le droit `auditLogs_read` est porté ICI, une fois pour toutes les pages : sans lui la section est
 * verrouillée et son contenu n'est pas monté — ni requête, ni souscription.
 */
const AUDIT_PERMISSIONS = [TotadminPermission.AuditLogsRead];

export const AuditLogsSection = ({
    collectionDocumentIds,
    collectionNames,
    subjectLabels,
    title = 'Historique des modifications',
}: {
    collectionDocumentIds?: string[];
    collectionNames?: string[];
    subjectLabels?: SubjectLabels;
    title?: string;
}) => {
    // Le lien mène à la vue transverse, filtrée sur le même sujet que la section — un document quand
    // la page en a un, sa collection sinon.
    const scope = collectionDocumentIds?.length
        ? `collectionDocumentId=${collectionDocumentIds[0]}`
        : `collectionName=${collectionNames?.[0] ?? ''}`;

    return (
        <SectionContainer
            title={title}
            lazy
            // La section plafonne à 100 lignes : au-delà, c'est la vue transverse qui prend.
            linkToMore={`/audit?${scope}`}
            linkToMoreLabel="tout l’historique"
            requiredPermissions={AUDIT_PERMISSIONS}
        >
            <AuditLogs
                collectionDocumentIds={collectionDocumentIds}
                collectionNames={collectionNames}
                subjectLabels={subjectLabels}
            />
        </SectionContainer>
    );
};
